import axios from "axios";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Config from "../../Config";
import {
  filterGames,
  splitFeaturedAndGrid,
} from "../../components/blocks/Games/gamesUtils";
import { get_user } from "../../components/blocks/profileUpdating/dataUpdating/ProfileDataUpdatingBlock";
import { setUser } from "../../store/UserSlice";
import { useWindowDimensions } from "../../utils";
import PersonalDefaultPage from "./PersonalDefaultPage";

import { MIN_WIDTH_FOR_MENU } from "../../components/NavBar";
import FeaturedGameBanner from "../../components/blocks/Games/FeaturedGameBanner";
import GameCoverTile from "../../components/blocks/Games/GameCoverTile";
import ViewGameBlock from "../../components/blocks/Games/ViewGameBlock";
import { GAMES_CATALOG } from "../../components/blocks/Games/gamesCatalog";
import {
  GAMES_CONTENT_OFFSET_NARROW,
  GAMES_CONTENT_OFFSET_WIDE,
  GAMES_CONTENT_WIDE_BREAKPOINT,
  GAMES_PAGE_TITLE_BREAKPOINT,
} from "../../components/blocks/Games/gamesConstants";
import { gamesPageStyles } from "../../components/blocks/Games/gamesStyles";
import NoFilesPlaceholder from "../../components/blocks/Library/NoFilesPlaceholder";
import SearchBar from "../../components/inputs/FormTextInput/SearchBar";
import { OnestBoldMed } from "../../components/styled/TextComponents";
import { PageTitle } from "../../components/text/PageTitle";
import { isDesktop } from "../../utilities/getDeviceType";
import { useI18n } from "../../i18n/I18nProvider";

export const GamesPage = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchValue, setSearchValue] = useState("");
  const { width: screenWidth } = useWindowDimensions();
  const user = useSelector((state) => state.user);

  // Локальный список игр для страницы (отсортированная копия GAMES_CATALOG).
  // Не путать с Redux state.libraryFiles — это другая сущность (библиотека материалов).
  const [games, setGames] = useState(GAMES_CATALOG);

  /**
   * Подготавливает каталог при монтировании страницы.
   * Не ходит в API — только сортирует по index.
   * Название loadGames вместо fetchLibrary: fetch = запрос на сервер, здесь его нет.
   */
  const loadGames = useCallback(() => {
    const sortedGames = [...GAMES_CATALOG].sort((a, b) => a.index - b.index);
    setGames(sortedGames);
  }, []);

  useEffect(() => {
    (async () => {
      if (!user || Object.keys(user).length === 0) {
        try {
          const data = await get_user();
          dispatch(setUser(data));
        } catch (error) {
          console.error("Error fetching user data:", error);
          // Обработка ошибки, например, перенаправление на страницу входа
          //navigate('/auth/login');
        }
      }
      loadGames();
    })();
  }, [dispatch, loadGames, user]);

  useEffect(() => {
    let backendAddr = Config.BACKEND_ADDR + "/payments/getSubscriptionPeriod"; // /api
    axios
      .get(backendAddr)
      .then((response) => {
        if (response.status === 200) {
          if (response.data.status === "not_paid") {
            navigate("/billing/");
          }
        }
      })
      .catch((err) => {
        console.warn("Ошибка проверки подписки в БД! ", err);
      });
  }, [navigate]);

  const isDesktopLayout = screenWidth >= MIN_WIDTH_FOR_MENU && isDesktop();

  const filteredGames = filterGames(games, searchValue);

  const { featuredGame, gridGames } = splitFeaturedAndGrid(filteredGames);

  return (
    <PersonalDefaultPage>
      <div style={gamesPageStyles.page}>
        {screenWidth >= GAMES_PAGE_TITLE_BREAKPOINT ? (
          <PageTitle title={t("Игры")} />
        ) : (
          <PageTitle fontSize={24} marginBottom={24} title={t("Игры")} />
        )}

        <SearchBar
          setSearchValue={(value) => setSearchValue(value)}
          searchValue={searchValue}
        />

        <div
          style={{
            ...gamesPageStyles.content,
            width: isDesktopLayout
              ? screenWidth < GAMES_CONTENT_WIDE_BREAKPOINT
                ? screenWidth - GAMES_CONTENT_OFFSET_NARROW
                : screenWidth - GAMES_CONTENT_OFFSET_WIDE
              : "100%",
          }}
        >
          {filteredGames.length === 0 ? (
            <NoFilesPlaceholder searchValue={searchValue} />
          ) : isDesktopLayout ? (
            <>
              {featuredGame && <FeaturedGameBanner game={featuredGame} />}

              <OnestBoldMed style={gamesPageStyles.sectionTitle}>
                {t("Другие игры")}
              </OnestBoldMed>

              <div style={gamesPageStyles.desktopGrid}>
                {gridGames.map((el) => (
                  <GameCoverTile key={el.id} game={el} />
                ))}
              </div>
            </>
          ) : (
            <div
              style={gamesPageStyles.mobileGrid(
                screenWidth >= GAMES_PAGE_TITLE_BREAKPOINT
                  ? "flex-start"
                  : "center",
              )}
            >
              {filteredGames.map((el) => (
                <ViewGameBlock key={el.id} game={el} />
              ))}
            </div>
          )}
        </div>
      </div>
    </PersonalDefaultPage>
  );
};
